import { memo, useCallback, useMemo, useState } from 'react'
import { z } from 'zod'
import { type Node, type NodeProps, Position } from '@xyflow/react'
import { nanoid } from 'nanoid'
import { useShallow } from 'zustand/shallow'
import { useFlowStore } from '@/stores/flow-store'
import { useDeleteNode } from '@/hooks/use-delete-node'
import CustomHandle from '@/features/flow/_builder/components/handles/custom-handler'
import {
  NodeCard,
  NodeCardContent,
  NodeCardDescription,
  NodeCardFooter,
  NodeCardHeader,
} from '@/features/flow/_builder/components/ui/node-card'
import RandomizerNodePropertyPanel from '../../sidebar/panels/node-properties/property-panels/randomizer-property-panel'
import { BuilderNode, RegisterNodeMetadata } from '../../types'
import { getNodeDetail } from '../../utils'

const NODE_TYPE = BuilderNode.RANDOMIZER

const RandomizerPathSchema = z.object({
  id: z.string(),
  label: z.string().min(1, 'Rótulo do caminho é obrigatório'),
  percentage: z
    .number()
    .min(0, 'Porcentagem deve ser pelo menos 0')
    .max(100, 'Porcentagem deve ser no máximo 100'),
})

const RandomizerNodeDataSchema = z
  .object({
    paths: z
      .array(RandomizerPathSchema)
      .min(2, 'Pelo menos dois caminhos devem ser configurados'),
    deletable: z.boolean().optional(),
  })
  .refine(
    (data) => {
      const totalPercentage = data.paths.reduce(
        (sum, path) => sum + path.percentage,
        0
      )
      return Math.abs(totalPercentage - 100) <= 0.01
    },
    { message: 'As porcentagens devem somar 100%', path: ['paths'] }
  )

export type RandomizerPath = z.infer<typeof RandomizerPathSchema>
export type RandomizerNodeData = z.infer<typeof RandomizerNodeDataSchema>

type RandomizerNodeProps = NodeProps<Node<RandomizerNodeData, typeof NODE_TYPE>>

export function RandomizerNode({
  id,
  isConnectable,
  selected,
  data,
}: RandomizerNodeProps) {
  const meta = useMemo(() => getNodeDetail(NODE_TYPE), [])

  const [showNodePropertiesOf] = useFlowStore(
    useShallow((s) => [s.actions.sidebar.showNodePropertiesOf])
  )

  const [targetHandleId] = useState<string>(`${id}-target`)

  const deleteNode = useDeleteNode()

  const handleDeleteNode = () => {
    deleteNode(id)
  }

  const handleShowNodeProperties = useCallback(() => {
    showNodePropertiesOf({ id, type: NODE_TYPE })
  }, [id, showNodePropertiesOf])

  return (
    <NodeCard
      data-selected={selected}
      onDoubleClick={handleShowNodeProperties}
      nodeId={id}
    >
      <NodeCardHeader
        icon={meta.icon}
        title={meta.title}
        handleDeleteNode={handleDeleteNode}
        handleShowNodeProperties={handleShowNodeProperties}
        gradientColor={meta.gradientColor}
      />

      <NodeCardContent>
        <div className='flex flex-col p-4'>
          <div className='text-xs font-medium text-card-foreground'>
            Caminhos
          </div>
          <div className='mt-2 text-sm leading-snug'>
            {data.paths.map((p) => (
              <div key={p.id} className='flex items-center justify-between'>
                <span className='truncate'>{p.label}</span>
                <span className='ml-2 rounded bg-card-foreground/10 px-1 py-0.5 text-xs'>
                  {p.percentage}%
                </span>
              </div>
            ))}
          </div>
        </div>

        <NodeCardDescription description='Distribui aleatoriamente para uma das saídas conforme porcentagens' />

        <NodeCardFooter nodeId={id} />
      </NodeCardContent>

      <CustomHandle
        type='target'
        id={targetHandleId}
        position={Position.Left}
        isConnectable={isConnectable}
      />

      {data.paths.map((p, idx) => (
        <CustomHandle
          key={p.id}
          type='source'
          id={p.id}
          position={Position.Right}
          className={
            idx === 0 ? '-mt-8' : idx === data.paths.length - 1 ? 'mt-8' : ''
          }
          isConnectable={isConnectable}
        />
      ))}
    </NodeCard>
  )
}

export const metadata: RegisterNodeMetadata<RandomizerNodeData> = {
  type: NODE_TYPE,
  node: memo(RandomizerNode),
  detail: {
    icon: 'shuffle',
    title: 'Randomizador',
    description: 'Cria caminhos aleatórios por porcentagens (A/B testing).',
    gradientColor: 'indigo',
  },
  connection: {
    inputs: 1,
    outputs: 2,
  },
  defaultData: {
    paths: [
      { id: nanoid(), label: 'A', percentage: 50 },
      { id: nanoid(), label: 'B', percentage: 50 },
    ],
  },
  propertyPanel: RandomizerNodePropertyPanel,
  validationSchema: RandomizerNodeDataSchema,
}
